export const meta = {
	slug: 'lorenz-86',
	title: 'Lorenz 86: the other Lorenz cloud',
	tag: 'Attractors',
	excerpt:
		'Lorenz 86 is a mid-1980s atmosphere ODE set: a folded 3D cloud on Classical Chaos, not the 1963 butterfly.',
	dateLabel: 'Aug 2026',
	readMinutes: 5,
	featured: true,
	thumbSlug: 'lorenz_86',
	order: 4,
}


Edward Lorenz’s famous butterfly is the 1963 convection model. Lorenz 86 is a later low-order atmosphere system: five knobs, a compact folded cloud, and no twin lobes. Same name on the label, different ODEs.

Classical Chaos draws it with Euler steps into a coloured point cloud. The classic butterfly still has its [own stage](/lorenz_attractor).

<VizEmbed slug="lorenz_86" label="Lorenz 86" animateN />

## The ODEs

Four real coefficients plus step size: `a`, `b`, `f`, `g`, and `d` (the Euler step).

<BlockMath math={'\\dot{x} = -a x - y^2 - z^2 + a b'} />
<BlockMath math={'\\dot{y} = -y + x y - f x z + g'} />
<BlockMath math={'\\dot{z} = -z + f x y + x z'} />

Defaults on Classical Chaos: `a=1.111`, `b=4.494`, `f=1.479`, `g=0.44`, `d=0.13`, seed `(0, 0, 0)`. The constant drive terms (`a b` in \(x\), `g` in \(y\)) push the state off the origin immediately. Drawn as a point cloud with HSL colouring from \(\sin(xy)\) — chunky Euler step and all.

## Why it is not the butterfly

The 1963 system stretches and folds into two lobes. Here the quadratic terms in \(x\) and the bilinear coupling through \(f\) keep a single tangled cloud. Nudge `f` and the twist changes. Nudge `a`/`b` and the overall scale of the drive shifts. Nothing is a mesh; it is only the orbit you integrate.

## On the full stage

Point count grows, scrubs, or holds like Lorenz and Aizawa. Change any of `a`, `b`, `f`, `g`, or `d` and the cloud resets from the seed.

```ts
x = -a * x - y * y - z * z + a * b
y = -y + x * y - f * x * z + g
z = -z + f * x * y + x * z
```

<Callout to="/lorenz_86">
	<strong>Goto full canvas</strong>
	Retune `a`, `b`, `f`, `g`, and `d`, scrub point count, try a preset, drag to rotate.
</Callout>
